import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Receipt, FileSignature, ClipboardCheck, Landmark, Wallet, HandCoins, Banknote, BookOpen } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { money } from "@/components/finance/shared";
import InvoicesTab from "@/components/finance/InvoicesTab";
import ReceivablesTab from "@/components/finance/ReceivablesTab";
import CollectionsTab from "@/components/finance/CollectionsTab";
import ApprovalTab from "@/components/finance/ApprovalTab";
import LiquidationTab from "@/components/finance/LiquidationTab";
import TransmittalsTab from "@/components/finance/TransmittalsTab";
import PayablesTab from "@/components/finance/PayablesTab";
import FinanceReports from "@/components/finance/FinanceReports";
import CashBookTab from "@/components/finance/CashBookTab";
import CashPositionTab from "@/components/finance/CashPositionTab";

const FOLDERS = [
  { key: "accounts", label: "Accounts", hint: "Accounts Receivable and Accounts Payable", icon: Landmark },
  { key: "expenses", label: "Expenses", hint: "Requests & vouchers, and liquidation", icon: Wallet },
  { key: "cash", label: "Cash", hint: "Daily cash book and cash position", icon: Banknote },
];

const GROUPS = [
  { key: "billing", folder: "accounts", label: "Accounts Receivable", hint: "Billing & collection: transmittals, invoices, dues, collections, VAT", icon: Receipt,
    tabs: [["transmittals", "Billing Transmittals"], ["invoicing", "Billed Clients & Invoices"], ["receivables", "Dues / Receivables"],
      ["collections", "Collections"], ["reports", "VAT Summary & Reports"]] },
  { key: "payable", folder: "accounts", label: "Accounts Payable", hint: "Supplier quotations and invoices to pay", icon: HandCoins,
    tabs: [["quotations", "Quotations"], ["payables", "Payables"]] },
  { key: "requests", folder: "expenses", label: "Request & Voucher", hint: "Operational fund, cash and check vouchers", icon: FileSignature,
    tabs: [["fund-requests", "Operational Fund"], ["cash-vouchers", "Cash Voucher"], ["check-vouchers", "Check Voucher"]] },
  { key: "liquidation", folder: "expenses", label: "Liquidation", hint: "Actual expenses against approved funds", icon: ClipboardCheck,
    tabs: [["liquidations", "Liquidation"]] },
  { key: "cashbook", folder: "cash", label: "Cash Book", hint: "Daily beginning, additional funds, payouts and closing", icon: BookOpen,
    tabs: [["cash-book", "Daily Cash Book"], ["cash-position", "Cash Position"]] },
];
const TABS = GROUPS.flatMap((g) => g.tabs);

const Stat = ({ label, value, testid, accent = "text-slate-100" }) => (
  <div className="glass-card rounded-lg p-4">
    <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div data-testid={testid} className={`font-mono-ops text-xl sm:text-2xl mt-1 ${accent}`}>{value}</div>
  </div>
);

export default function Finance() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "transmittals";
  const [sum, setSum] = useState(null);
  const loadSummary = () => api.get("/finance/summary").then((r) => setSum(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { loadSummary(); }, []);

  const pending = sum ? Object.values(sum.pending).reduce((a, b) => a + b, 0) : 0;
  const badge = (k) => (sum?.pending?.[k] ? <span className="ml-1.5 text-[10px] px-1.5 rounded bg-amber-500/20 text-amber-300">{sum.pending[k]}</span> : null);
  const group = GROUPS.find((g) => g.tabs.some(([k]) => k === tab));
  const groupPending = (g) => g.tabs.reduce((a, [k]) => a + (sum?.pending?.[k] || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-sky-400 mb-1">Finance</div>
        <h1 className="font-display text-4xl font-bold">Finance</h1>
        <p className="text-slate-400 text-sm mt-1">Billing & collection, requests & vouchers, liquidation, and the daily cash book.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total billed" value={money(sum?.total_billed)} testid="fin-total-billed" />
        <Stat label="Outstanding" value={money(sum?.total_outstanding)} testid="fin-outstanding" accent="text-sky-300" />
        <Stat label="Collected this month" value={money(sum?.collected_this_month)} testid="fin-collected-month" accent="text-emerald-300" />
        <Stat label="Awaiting approval" value={pending} testid="fin-pending" accent={pending ? "text-amber-300" : "text-slate-100"} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {FOLDERS.map((fo) => {
          const active = fo.key === group.folder;
          const Icon = fo.icon;
          const first = GROUPS.find((g) => g.folder === fo.key);
          return (
            <button key={fo.key} data-testid={`fin-folder-${fo.key}`} onClick={() => setParams({ tab: first.tabs[0][0] })}
                    className={`text-left rounded-lg border p-4 transition-colors ${active ? "border-sky-500/60 bg-sky-500/10" : "border-slate-800 bg-slate-900/40 hover:bg-slate-900"}`}>
              <div className={`flex items-center gap-2 font-display text-lg font-bold ${active ? "text-sky-300" : "text-slate-200"}`}><Icon size={18} /> {fo.label}</div>
              <div className="text-xs text-slate-500 mt-1">{fo.hint}</div>
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {GROUPS.filter((g) => g.folder === group.folder).map((g) => {
          const active = g.key === group.key;
          const Icon = g.icon;
          return (
            <button key={g.key} data-testid={`fin-group-${g.key}`} onClick={() => setParams({ tab: g.tabs[0][0] })}
                    className={`text-left rounded-lg border p-4 transition-colors ${active
                      ? "border-blue-500/50 bg-blue-600/10" : "border-slate-800 bg-slate-900/40 hover:bg-slate-900"}`}>
              <div className="flex items-center justify-between">
                <div className={`flex items-center gap-2 font-display font-bold ${active ? "text-sky-300" : "text-slate-200"}`}>
                  <Icon size={16} /> {g.label}
                </div>
                {groupPending(g) > 0 && <span className="text-[10px] px-1.5 rounded bg-amber-500/20 text-amber-300">{groupPending(g)} pending</span>}
              </div>
              <div className="text-xs text-slate-500 mt-1">{g.hint}</div>
            </button>
          );
        })}
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        {group.tabs.length > 1 && (
          <div className="overflow-x-auto">
            <TabsList className="bg-slate-900 border border-slate-800 h-auto p-1 inline-flex w-max gap-1">
              {group.tabs.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`fin-tab-${k}`}>{l}{badge(k)}</TabsTrigger>)}
            </TabsList>
          </div>
        )}
        <TabsContent value="reports" className="mt-5"><FinanceReports /></TabsContent>
        <TabsContent value="quotations" className="mt-5"><PayablesTab kind="quotation" /></TabsContent>
        <TabsContent value="payables" className="mt-5"><PayablesTab kind="bill" /></TabsContent>
        <TabsContent value="transmittals" className="mt-5"><TransmittalsTab onChanged={loadSummary} /></TabsContent>
        <TabsContent value="invoicing" className="mt-5"><InvoicesTab onChanged={loadSummary} /></TabsContent>
        <TabsContent value="receivables" className="mt-5"><ReceivablesTab /></TabsContent>
        <TabsContent value="collections" className="mt-5"><CollectionsTab onChanged={loadSummary} /></TabsContent>
        {["fund-requests", "cash-vouchers", "check-vouchers"].map((k) => (
          <TabsContent key={k} value={k} className="mt-5"><ApprovalTab kind={k} onChanged={loadSummary} /></TabsContent>
        ))}
        <TabsContent value="liquidations" className="mt-5"><LiquidationTab onChanged={loadSummary} /></TabsContent>
        <TabsContent value="cash-book" className="mt-5"><CashBookTab /></TabsContent>
        <TabsContent value="cash-position" className="mt-5"><CashPositionTab /></TabsContent>
      </Tabs>
    </div>
  );
}
